import { useEffect, useState } from 'react';
import { Box, CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import { MdOutlineLocalPhone } from 'react-icons/md';
import SipIcon from '@/assets/icons/SipIcon';
import PromptBuilderEmptyStateIcon from '@/assets/icons/PromptBuilderEmptyStateIcon';
import Button from 'components/Common/Button';
import * as urls from 'routes/config';
import { useGetTelephonyEndpoints } from '@/apis/business/setting/telephonyEndpoints/hook';
import { TelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/types';
import TelephonyNumbersList from './components/TelephonyNumbersList';
import TelephonyNumberDetail from './components/TelephonyNumberDetail';
import BuyNumberDrawer from './components/BuyNumberDrawer';
import ConnectViaSipDrawer from './components/ConnectViaSipDrawer';

const NUMBERS_PATH = `${urls.business.base}/${urls.business.telephony}/numbers`;

export default function PhoneNumbers() {
  const theme = useTheme();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const [selected, setSelected] = useState<TelephonyEndpoint | null>(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [sipOpen, setSipOpen] = useState(false);
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);

  const { data, isLoading } = useGetTelephonyEndpoints({ page: 1, per_page: 100 });
  const numbers = data?.data ?? [];
  const isEmpty = Boolean(data) && !isLoading && numbers.length === 0;

  useEffect(() => {
    const rows = data?.data;
    if (!data) return;
    if (!rows?.length) {
      setSelected(null);
      return;
    }
    setSelected((prev) => {
      if (prev && rows.some((row) => row.id === prev.id)) {
        return rows.find((row) => row.id === prev.id) ?? prev;
      }
      return rows[0];
    });
  }, [data]);

  if (!tabParam || tabParam !== 'numbers') {
    return <Navigate to={NUMBERS_PATH} replace />;
  }

  const addMenu = (
    <Menu
      anchorEl={addAnchor}
      open={Boolean(addAnchor)}
      onClose={() => setAddAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          mt: 1,
          minWidth: 300,
          p: 1,
          borderRadius: '6px',
          border: '1px solid #ECECEC',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
        }
      }}
    >
      <MenuItem
        onClick={() => {
          setAddAnchor(null);
          setBuyOpen(true);
        }}
        sx={{
          py: 1.5,
          px: 1.5,
          mb: 0.75,
          alignItems: 'center',
          gap: 1.25,
          borderRadius: '6px',
          bgcolor: '#F8F9FA',
          '&:hover': { bgcolor: '#EBEBEF' }
        }}
      >
        <ListItemIcon sx={{ minWidth: 28, color: theme.palette.primary.main }}>
          <MdOutlineLocalPhone size={22} />
        </ListItemIcon>
        <ListItemText
          primary='Buy Number'
          secondary='Choose a Twilio & Bandwidth Number'
          primaryTypographyProps={{ fontWeight: 700, fontSize: 14, color: '#111827' }}
          secondaryTypographyProps={{ fontSize: 12, color: '#6B7280' }}
        />
      </MenuItem>
      <MenuItem
        onClick={() => {
          setAddAnchor(null);
          setSipOpen(true);
        }}
        sx={{
          py: 1.5,
          px: 1.5,
          alignItems: 'center',
          gap: 1.25,
          borderRadius: '6px',
          bgcolor: '#F8F9FA',
          '&:hover': { bgcolor: '#EBEBEF' }
        }}
      >
        <ListItemIcon sx={{ minWidth: 28, color: theme.palette.primary.main }}>
          <SipIcon width={22} height={22} />
        </ListItemIcon>
        <ListItemText
          primary='Connect via SIP'
          secondary='SIP registration or SIP Trunking'
          primaryTypographyProps={{ fontWeight: 700, fontSize: 14, color: '#111827' }}
          secondaryTypographyProps={{ fontSize: 12, color: '#6B7280' }}
        />
      </MenuItem>
    </Menu>
  );

  const addButton = (
    <Button
      variant='contained'
      color='primary'
      size='small'
      startIcon={<FiPlus size={16} strokeWidth={2.5} />}
      onClick={(e) => setAddAnchor(e.currentTarget)}
      sx={{
        textTransform: 'none',
        borderRadius: '999px',
        px: 2,
        py: 0.85,
        fontWeight: 500,
        fontSize: 14,
        minHeight: 34,
        lineHeight: 1.2,
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        minWidth: 'auto',
        bgcolor: theme.palette.primary.main,
        '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' }
      }}
    >
      Add
    </Button>
  );

  return (
    <>
      {isLoading && !data ? (
        <Box display='flex' justifyContent='center' alignItems='center' minHeight={360}>
          <CircularProgress size={32} />
        </Box>
      ) : isEmpty ? (
        <Box>
          <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={2} mb={2.5}>
            <Box minWidth={0}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 22,
                  lineHeight: 1.2,
                  color: '#111827',
                  letterSpacing: '-0.01em'
                }}
              >
                Numbers
              </Typography>
              <Typography color='text.secondary' sx={{ fontSize: 13, mt: 0.75, lineHeight: 1.4 }}>
                Phone numbers and extensions assigned to your agents.
              </Typography>
            </Box>
            {addButton}
          </Box>

          <Box
            bgcolor='#fff'
            border='1px solid #E5E7EB'
            borderRadius='8px'
            minHeight={420}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            textAlign='center'
            px={3}
            py={6}
          >
            <Box sx={{ '& svg': { width: 160, height: 'auto' }, opacity: 0.9, mb: 1 }}>
              <PromptBuilderEmptyStateIcon />
            </Box>
            <Typography fontWeight={700} fontSize={18} color='#111827' mb={0.75}>
              No Numbers Added Yet
            </Typography>
            <Typography fontSize={14} color='#6B7280' maxWidth={420} lineHeight={1.5}>
              Add a phone number or extension to start routing calls and SMS to your agents.
            </Typography>
          </Box>
          {addMenu}
        </Box>
      ) : (
        <Box display='flex' gap={2.5} alignItems='flex-start' flexDirection={{ xs: 'column', md: 'row' }}>
          <TelephonyNumbersList
            selectedId={selected?.id ?? null}
            onSelect={setSelected}
            onBuyNumber={() => setBuyOpen(true)}
            onConnectSip={() => setSipOpen(true)}
          />
          <TelephonyNumberDetail number={selected} onDeleted={() => setSelected(null)} />
        </Box>
      )}

      <BuyNumberDrawer open={buyOpen} onClose={() => setBuyOpen(false)} />
      <ConnectViaSipDrawer open={sipOpen} onClose={() => setSipOpen(false)} />
    </>
  );
}
